import {useKeycloakHeader} from "../config/Config";
import {PlaywrightConfigurationControllerApi, PlaywrightConfigurationDto} from "../api";
import React, {useEffect, useState} from "react";
import {Button, message, Popconfirm, Table} from "antd";
import Title from "antd/lib/typography/Title";
import ConfigurationModal from "../components/modal/ConfigurationModal";
import {CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';


const PlaywrightConfigurationOverview = () => {
    const headers = useKeycloakHeader();
    const configurationControllerApi = new PlaywrightConfigurationControllerApi();

    const [configurations, setConfigurations] = useState<PlaywrightConfigurationDto[]>([]);
    const [activeConfiguration, setActiveConfiguration] = useState<PlaywrightConfigurationDto | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editData, setEditData] = useState<PlaywrightConfigurationDto | null>(null);

    useEffect(() => {
        fetchConfigurations();
    }, [])

    const fetchConfigurations = () => {
        configurationControllerApi.getPlaywrightConfigurations(headers)
            .then(response => {
                const data: PlaywrightConfigurationDto[] = response.data;
                setConfigurations(data);
                let activeConfig = data.filter(configuration => configuration.active);
                if (activeConfig.length > 0) {
                    setActiveConfiguration(activeConfig[0]);
                }
            })
            .catch(error => {
                message.error("Es konnten keine Konfigurationen geladen werden!");
            });
    }

    const handleActivate = (id: number) => {
        configurationControllerApi.activateConfiguration(id, headers)
            .then(() => {
                message.success("Konfiguration aktiviert");
                fetchConfigurations();
            })
            .catch(() => {
                message.error("Aktivierung fehlgeschlagen");
            });
    };

    const openEditModal = (record?: PlaywrightConfigurationDto) => {
        setEditData(record || null);
        setEditModalVisible(true);
    };

    const handleSaveConfiguration = (values: PlaywrightConfigurationDto) => {
        setLoading(true);

        if (editData) {
            let request = {id: editData.id, ...values};
            configurationControllerApi.updateConfiguration(request, headers).then(() => {
                message.success('Konfiguration gespeichert!');
                setEditModalVisible(false);
                fetchConfigurations();
            }).catch(() => {
                message.error('Fehler beim Speichern!');
            }).finally(() => {
                setLoading(false);
            });
        } else {
            configurationControllerApi.createConfiguration(values, headers).then(() => {
                message.success('Konfiguration gespeichert!');
                setEditModalVisible(false);
                fetchConfigurations();
            }).catch(() => {
                message.error('Fehler beim Speichern!');
            }).finally(() => {
                setLoading(false);
            });
        }

    };

    const handleDeleteConfiguration = (dto: PlaywrightConfigurationDto) => {
        if (dto.id) {
            configurationControllerApi.deleteConfiguration(dto.id, headers).then(() => {
                message.success('Konfiguration gelöscht!');
                fetchConfigurations();
            }).catch(() => {
                message.error('Fehler beim Löschen!');
            })
        }
    }


    const columns = [
        {title: 'ID', dataIndex: 'id', key: 'id'},
        {title: 'Name', dataIndex: 'configName', key: 'configName'},
        {
            title: 'Headless',
            dataIndex: 'headless',
            key: 'headless',
            render: (val: boolean) => val ? 'Ja' : 'Nein'
        },
        {
            title: 'Viewport',
            key: 'viewport',
            render: (_: any, record: PlaywrightConfigurationDto) =>
                `${record.viewPortWidth} x ${record.viewPortHeight}`
        },
        {title: 'Locale', dataIndex: 'locale', key: 'locale'},

        // 🆕 NEU: Proxy-Spalte
        {
            title: 'Proxy',
            key: 'proxy',
            render: (_: any, record: PlaywrightConfigurationDto) => {
                const proxy = record.proxy;
                if (!proxy || !proxy.server) {
                    return <span style={{color: 'gray'}}>Kein Proxy</span>;
                }
                return (
                    <div>
                        <div><strong>Server:</strong> {proxy.server}</div>
                        {proxy.username && <div><strong>User:</strong> {proxy.username}</div>}
                        {proxy.bypass && <div><strong>Bypass:</strong> {proxy.bypass}</div>}
                    </div>
                );
            }
        },
        {
            title: 'Aktionen',
            key: 'actions',
            render: (_: any, record: PlaywrightConfigurationDto) => (
                <>
                    <Button onClick={() => openEditModal(record)}><EditOutlined
                        style={{fontSize: '16px', color: '#1890ff'}}/></Button>
                    <Popconfirm
                        title="Diese Konfiguration löschen?"
                        onConfirm={() => handleDeleteConfiguration(record)}
                        okText="Ja"
                        cancelText="Nein"
                    >
                        <Button><DeleteOutlined
                            style={{fontSize: '16px', color: "red"}}/></Button>
                    </Popconfirm>
                    {!record.active && (
                        <Popconfirm
                            title="Diese Konfiguration aktivieren?"
                            onConfirm={() => handleActivate(record.id!)}
                            okText="Ja"
                            cancelText="Nein"
                        >
                            <Button type="link"><CheckOutlined style={{color: '#52c41a'}}/></Button>
                        </Popconfirm>
                    )}
                </>
            )
        }
    ];

    return (
        <div>
            <div
                style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: 16}}>
                <Title level={3} style={{margin: 0}}>Aktive Konfiguration</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined/>}
                    onClick={() => {
                        setEditData(null);
                        openEditModal();
                    }}
                >
                    Neue Konfiguration
                </Button>
            </div>

            <Table
                dataSource={activeConfiguration ? [activeConfiguration] : []}
                columns={columns}
                rowKey="id"
                pagination={false}
                loading={loading}
                locale={{emptyText: 'Keine aktive Konfiguration'}}
            />

            <Title level={4} style={{marginTop: 40}}>Andere Konfigurationen</Title>
            <Table
                dataSource={configurations.filter(cfg => !cfg.active)}
                columns={columns}
                rowKey="id"
                loading={loading}
                locale={{emptyText: 'Keine weiteren Konfigurationen'}}
            />

            <ConfigurationModal
                visible={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onSave={handleSaveConfiguration}
                loading={loading}
                initialData={editData}
            />
        </div>
    );

}
export default PlaywrightConfigurationOverview;